import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ExternalLink, MapPin, Calendar, Building2, Euro, AlertTriangle, Phone, Mail, ArrowLeft, ArrowRight, Loader2, Sparkles, FileText, MessageCircle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import SEO, { breadcrumb } from "../components/SEO";
import { normalizeBoeAddress, normalizeLocalidad } from "../utils/address";
import Countdown from "../components/Countdown";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { trackEvent, EVENTS } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtEur(n) {
  if (n == null) return "—";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function SubastaDetail() {
  const params = useParams();   // puede tener `id` o `loc`+`slug`
  // Si viene la ruta /subastas/:loc/:slug usamos el slug, que lleva el ID al final.
  // Si viene /subastas/:id, ese id puede ser ID puro o slug con ID al final.
  const id = params.slug || params.id;
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/api/subastas/${id}`)
      .then(r => { if (mounted) setS(r.data); })
      .catch(e => { if (mounted) setError(e?.response?.status === 404 ? "Subasta no encontrada" : "Error cargando subasta"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="min-h-screen bg-[#1a110e] text-stone-200 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E0651F]" /></div>;
  if (error || !s) return (
    <div className="min-h-screen bg-[#1a110e] text-stone-200">
      {/* SEO: dile a Google que esta variante NO debe indexarse. Sin esto,
          las URLs de subastas eliminadas/no accesibles se marcan como
          "soft 404" en GSC. Con noindex + un mensaje claro, Google las
          desindexa limpiamente. */}
      <SEO
        title="Subasta no encontrada · Fernando Marcos Subastas & Investment"
        description="La subasta que buscas ya no está disponible o su URL es incorrecta. Explora todas las subastas activas del BOE en el buscador."
        path={`/subastas/${id}`}
        noindex
      />
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="subasta-not-found">
        <h1 className="text-3xl font-display font-black mb-4">{error || "Esta subasta ya no está disponible"}</h1>
        <p className="text-stone-400 mb-6 max-w-xl mx-auto">
          Puede que se haya archivado o su URL ya no sea válida. Sigue todas las subastas activas del BOE actualizadas a diario.
        </p>
        <Link to="/subastas" className="inline-flex items-center gap-2 text-[#E0651F] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Volver al buscador de subastas
        </Link>
      </div>
      <SiteFooter />
    </div>
  );

  const d = s.detalle?.datos_subasta || {};
  const b = s.detalle?.bien || {};
  const ag = s.detalle?.autoridad_gestora || {};
  const descBien = b.descripcion || s.descripcion;
  const realId = s.identificador;
  const slug = s.slug || realId;
  // URL canonical preferida (SEO): la del backend si existe, fallback al slug.
  const canonicalPath = s.url_seo || `/subastas/${slug}`;
  const direccionRaw = b.direccion || s.direccion_corta || "";
  const direccion = normalizeBoeAddress(direccionRaw);
  const localidadRaw = b.localidad || s.provincia || "";
  const localidad = normalizeLocalidad(localidadRaw);
  // SEO: si subtipo es "Otros inmuebles" (599) usamos "Inmueble" más neutro
  const tipoLabel = (s.subtipo === "599" || !s.subtipo_nombre) ? "Inmueble" : s.subtipo_nombre;
  // Construir título y meta SEO con la dirección (clave para Google)
  const seoTitleParts = [tipoLabel];
  if (direccion) seoTitleParts.push(direccion);
  if (localidad && localidad.toLowerCase() !== (s.provincia || "").toLowerCase()) seoTitleParts.push(localidad);
  if (s.provincia) seoTitleParts.push(s.provincia);
  const seoTitle = seoTitleParts.join(" · ") + " · Subasta BOE";
  // Meta description enriquecida (RCA soft-404 Feb 2026): antes era muy corta
  // ("Subasta X BOE de vivienda. VIVIENDA EN Y (Z)") lo que Google considera
  // "thin content". Ahora añadimos valor de subasta, fecha de conclusión y
  // organismo si están disponibles.
  const valorSubastaEur = d.valor_subasta || s.valor_subasta;
  const fechaFinISO = d.fecha_fin || s.fecha_fin;
  const fechaFinLabel = (() => {
    if (!fechaFinISO) return "";
    try {
      const dt = new Date(fechaFinISO);
      return dt.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
  })();
  const seoDescParts = [
    `Subasta ${s.tipo || ""} BOE de ${tipoLabel.toLowerCase()}${direccion ? " en " + direccion : ""}${localidad ? ", " + localidad : ""}${s.provincia && s.provincia !== localidad ? " (" + s.provincia + ")" : ""}.`,
    valorSubastaEur ? `Valor de subasta: ${Number(valorSubastaEur).toLocaleString("es-ES")} €.` : "",
    fechaFinLabel ? `Finaliza el ${fechaFinLabel}.` : "",
    ag?.codigo ? `Convoca: ${ag.codigo}.` : "",
    "Datos oficiales del Portal de Subastas del BOE. Análisis y asesoría profesional disponible.",
  ].filter(Boolean);
  const seoDesc = seoDescParts.join(" ").slice(0, 300);

  return (
    <div className="min-h-screen bg-[#1a110e] text-stone-200">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={canonicalPath}
        keywords={`subasta BOE, ${s.subtipo_nombre || ""}, ${direccion || ""}, ${localidad || ""}, ${s.provincia || ""}, subasta inmobiliaria`}
        jsonLd={[
          breadcrumb([
            { name: "Inicio", path: "/" },
            { name: "Subastas BOE", path: "/subastas" },
            { name: `${s.subtipo_nombre || "Subasta"}${localidad ? " en " + localidad : ""}`, path: canonicalPath },
          ]),
          // PostalAddress structured data — clave para SEO local
          ...(direccion || localidad ? [{
            "@context": "https://schema.org",
            "@type": "Place",
            name: `${s.subtipo_nombre || "Inmueble"} en subasta`,
            address: {
              "@type": "PostalAddress",
              streetAddress: direccion || undefined,
              addressLocality: localidad || undefined,
              postalCode: b.codigo_postal || undefined,
              addressRegion: s.provincia || undefined,
              addressCountry: "ES",
            },
          }] : []),
        ]}
      />
      <SiteHeader />

      {/* Breadcrumb + back */}
      <div className="px-6 pt-8 pb-2 max-w-6xl mx-auto">
        <Link to="/subastas" className="text-xs text-stone-500 hover:text-white inline-flex items-center gap-1.5" data-testid="subasta-detail-back">
          <ArrowLeft className="w-3 h-3" /> Volver al buscador
        </Link>
        {/* Si es un LOTE de una subasta multi-lote, link al padre */}
        {s.parent_id && (
          <div className="mt-2 text-xs text-stone-400">
            <Link
              to={`/subastas/${s.parent_id}`}
              className="text-[#E0651F] hover:underline inline-flex items-center gap-1"
              data-testid="link-back-to-parent"
            >
              <ArrowLeft className="w-3 h-3" /> Ver los {s.total_lotes} lotes de esta subasta
            </Link>
          </div>
        )}
      </div>

      {/* HERO */}
      <section className="px-6 pt-4 pb-8 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-bold mb-3">
          <span className="text-[#E0651F]">{tipoLabel}</span>
          <span className="text-stone-600">·</span>
          <span className="text-stone-500">Subasta {s.tipo}</span>
          {s.lote_num ? (
            <>
              <span className="text-stone-600">·</span>
              <span className="text-[#E0651F] font-bold">Lote {s.lote_num}/{s.total_lotes}</span>
            </>
          ) : null}
          {s.provincia && (<><span className="text-stone-600">·</span><span className="text-stone-500"><MapPin className="w-3 h-3 inline -mt-0.5" /> {s.provincia}</span></>)}
          <DetailStatusBadge status={s.status} />
        </div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-white leading-tight break-words">
          {tipoLabel}
          {direccion ? ` en ${direccion}` : ""}
          {!direccion && b.localidad ? ` en ${normalizeLocalidad(b.localidad)}` : ""}
          {!direccion && !b.localidad && s.provincia ? ` en ${s.provincia}` : ""}
        </h1>
        {(b.localidad || s.provincia) && direccion && (
          <div className="mt-2 text-stone-400 text-base">
            <MapPin className="w-4 h-4 inline -mt-0.5 mr-1" />
            {b.localidad ? `${normalizeLocalidad(b.localidad)}, ` : ""}{s.provincia}
            {b.codigo_postal ? ` · CP ${b.codigo_postal}` : ""}
          </div>
        )}
        {descBien && (
          <p className="mt-4 text-stone-300 text-sm sm:text-base leading-relaxed max-w-3xl whitespace-pre-line">
            {descBien}
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-400">
          <span><span className="text-stone-500">ID BOE: </span><code className="text-stone-300">{realId}</code></span>
          <span><span className="text-stone-500">Estado: </span><span className="text-stone-300">{s.estado || "—"}</span></span>
          <a href={s.url_oficial} target="_blank" rel="noopener noreferrer" className="text-[#E0651F] hover:underline inline-flex items-center gap-1">
            Ver en BOE <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        {/* Cuenta atrás en vivo: aumenta CTR + percepción de urgencia.
            Solo se renderiza si la subasta está activa y tiene fecha_fin. */}
        {(s.activa !== false) && (d.fecha_fin || s.fecha_fin) && (
          <div className="mt-6">
            <Countdown fechaFin={d.fecha_fin || s.fecha_fin} variant="hero" />
          </div>
        )}
      </section>

      {/* === Si es padre multi-lote: lista de lotes y NO mostramos datos del bien individual === */}
      {s.is_parent && Array.isArray(s.lotes) && s.lotes.length > 0 ? (
        <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="lotes-list-section">
          <div className="bg-[#E0651F]/10 border border-[#E0651F]/40 rounded-sm p-5 mb-6">
            <h2 className="text-sm font-bold text-[#E0651F] uppercase tracking-[0.2em] mb-2">
              Subasta con {s.lotes.length} lotes independientes
            </h2>
            <p className="text-stone-300 text-sm leading-relaxed">
              Esta subasta se compone de <strong>{s.lotes.length} lotes</strong>. La participación, depósito y adjudicación son independientes para cada uno. Pulsa cada lote para ver sus datos completos (dirección, valor, tasación, puja mínima…).
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {s.lotes.map((lt) => {
              const ltBien = (lt.detalle?.bien) || {};
              const ltDir = ltBien.direccion || lt.direccion_corta || "";
              const ltLoc = ltBien.localidad || "";
              const ltDesc = ltBien.descripcion || lt.descripcion || "";
              const ltUrl = lt.url_seo || `/subastas/${lt.slug || lt.identificador}`;
              return (
                <Link
                  to={ltUrl}
                  key={lt.identificador}
                  className="block bg-stone-900 hover:bg-stone-800 border border-stone-800 hover:border-[#E0651F]/50 rounded-sm p-5 transition-colors group"
                  data-testid={`lote-card-${lt.lote_num}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F]">
                      Lote {lt.lote_num}/{s.lotes.length}
                    </span>
                    <DetailStatusBadge status={lt.status} />
                  </div>
                  <div className="text-white font-display font-bold text-base sm:text-lg leading-tight mb-2 line-clamp-2">
                    {lt.subtipo_nombre || "Inmueble"}{ltDir ? ` · ${ltDir}` : ""}
                  </div>
                  {ltLoc && (
                    <div className="text-xs text-stone-400 mb-2">
                      <MapPin className="w-3 h-3 inline -mt-0.5 mr-1" />
                      {ltLoc}
                    </div>
                  )}
                  {ltDesc && (
                    <p className="text-xs text-stone-500 line-clamp-3 mb-2">{ltDesc}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-400">
                    {ltBien.valor_subasta && (<span><span className="text-stone-500">Valor: </span>{fmtEur(ltBien.valor_subasta)}</span>)}
                    {ltBien.puja_minima && (<span><span className="text-stone-500">Puja mín.: </span>{fmtEur(ltBien.puja_minima)}</span>)}
                  </div>
                  <div className="mt-3 text-[#E0651F] text-xs font-bold inline-flex items-center gap-1 group-hover:underline">
                    Ver lote completo <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : (
      <section className="px-6 pb-16 max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
        {/* LEFT: data */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cifras económicas. Para LOTES (b.valor_subasta presente) se prioriza el valor del lote. */}
          {(b.valor_subasta || b.puja_minima || b.deposito || b.tasacion || d.valor_subasta || d.tasacion || d.puja_minima || d.deposito) && (
            <div className="bg-stone-900 border border-stone-800 rounded-sm p-6">
              <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4 flex items-center gap-2"><Euro className="w-3.5 h-3.5" /> Cifras económicas{s.lote_num ? ` · Lote ${s.lote_num}/${s.total_lotes}` : ""}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <DataItem label="Valor de subasta" value={fmtEur(b.valor_subasta ?? d.valor_subasta)} highlight />
                <DataItem label="Tasación" value={fmtEur(b.tasacion ?? d.tasacion)} />
                <DataItem label="Puja mínima" value={fmtEur(b.puja_minima ?? d.puja_minima)} highlight />
                <DataItem label="Depósito requerido" value={fmtEur(b.deposito ?? d.deposito)} />
                <DataItem label="Tramos entre pujas" value={fmtEur(d.tramos)} />
                <DataItem label="Anuncio BOE" value={d.anuncio_boe || "—"} />
              </div>
              {s.adjudicacion_directa != null && (
                <div className="mt-5 bg-[#E0651F]/10 border border-[#E0651F]/40 rounded-sm p-4" data-testid="ficha-adjudicacion-directa">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#E0651F] font-bold">Adjudicación directa</div>
                    <div className="text-white font-display font-black text-xl sm:text-2xl">
                      {fmtEur(s.adjudicacion_directa)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Calendario */}
          <div className="bg-stone-900 border border-stone-800 rounded-sm p-6">
            <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Calendario</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Fecha de inicio: NO usar fecha_fin como fallback (provocaba que
                  ambos campos mostraran la misma fecha cuando fecha_inicio era null).
                  Si no está, mostramos "—" (Feb 2026 RCA). */}
              <DataItem label="Fecha de inicio" value={d.fecha_inicio ? fmtDate(d.fecha_inicio) : "—"} />
              <DataItem label="Fecha de conclusión" value={fmtDate(d.fecha_fin || s.fecha_fin)} highlight />
            </div>
          </div>

          {/* Bien */}
          {(b.direccion || b.localidad || b.ref_catastral || b.idufir) && (
            <div className="bg-stone-900 border border-stone-800 rounded-sm p-6">
              <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Datos del inmueble</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <DataItem label="Dirección" value={direccion || "—"} />
                <DataItem label="Localidad" value={localidad || "—"} />
                <DataItem label="Código postal" value={b.codigo_postal || "—"} />
                <DataItem label="Referencia catastral" value={b.ref_catastral || "—"} mono />
                <DataItem label="IDUFIR" value={b.idufir || "—"} mono />
              </div>
              {b.ref_catastral && (() => {
                // Catastro URL: la sede actual requiere partir la RC en dos
                // trozos (rc1 = primeros 7 caracteres, rc2 = siguientes 7).
                // La URL antigua `OVCConCiud.aspx?RefC=...` redirige a
                // OVCErrorDatos.aspx (rota desde mid-2025). Usamos
                // `OVCListaBienes.aspx` que sí muestra la ficha.
                const rc = String(b.ref_catastral).trim().toUpperCase();
                const rc1 = rc.slice(0, 7);
                const rc2 = rc.slice(7, 14);
                const url = rc.length >= 14
                  ? `https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCListaBienes.aspx?rc1=${rc1}&rc2=${rc2}`
                  : `https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?refcat=${rc}`;
                return (
                  <a
                    href={url}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#E0651F] hover:underline mt-4"
                    data-testid="catastro-link"
                  >
                    Ver ficha en Catastro <ExternalLink className="w-3 h-3" />
                  </a>
                );
              })()}
            </div>
          )}

          {/* Mapa de localización (Google Maps embed sin API key).
              Prioridad de query:
                1. coords del Catastro (lat,lng) → máxima precisión
                2. dirección + localidad + provincia
                3. localidad + provincia (fallback)
          */}
          {(b.coords?.lat || b.direccion || b.localidad) && (() => {
            const hasCoords = b.coords && typeof b.coords.lat === "number" && typeof b.coords.lng === "number";
            const q = hasCoords
              ? `${b.coords.lat},${b.coords.lng}`
              : [b.direccion, b.localidad, b.codigo_postal, s.provincia, "España"].filter(Boolean).join(", ")
                || [b.localidad, s.provincia, "España"].filter(Boolean).join(", ");
            const precisionLabel = hasCoords
              ? "Ubicación exacta (Catastro)"
              : (b.direccion ? "Ubicación aproximada" : "Ubicación referencial");
            const mapsHref = hasCoords
              ? `https://www.google.com/maps?q=${b.coords.lat},${b.coords.lng}&z=18`
              : `https://www.google.com/maps?q=${encodeURIComponent(q)}`;
            const embedSrc = hasCoords
              ? `https://www.google.com/maps?q=${b.coords.lat},${b.coords.lng}&z=18&output=embed`
              : `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`;
            return (
              <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-hidden" data-testid="subasta-map">
                <div className="p-4 sm:p-5 flex items-center justify-between flex-wrap gap-2 border-b border-stone-800">
                  <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" /> {precisionLabel}
                  </h2>
                  <a
                    href={mapsHref}
                    target="_blank" rel="noopener noreferrer"
                    className="text-[10px] tracking-[0.18em] uppercase font-bold text-stone-400 hover:text-[#E0651F] inline-flex items-center gap-1"
                    data-testid="subasta-map-open"
                  >
                    Abrir en Google Maps <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <iframe
                  title={`Mapa de ${q}`}
                  src={embedSrc}
                  className="w-full h-72 sm:h-80 border-0 block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="px-4 sm:px-5 py-3 text-[11px] text-stone-500 leading-relaxed">
                  {hasCoords
                    ? "Coordenadas exactas obtenidas de la sede del Catastro a partir de la referencia catastral oficial."
                    : "La ubicación se calcula a partir de la dirección que el BOE/Registro publica. Puede ser aproximada — verifica siempre la referencia catastral antes de pujar."}
                </div>
              </div>
            );
          })()}

          {/* Autoridad */}
          {ag.descripcion && (
            <div className="bg-stone-900 border border-stone-800 rounded-sm p-6">
              <h2 className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-4">Autoridad gestora</h2>
              <p className="text-white font-bold mb-2">{ag.descripcion}</p>
              <div className="text-sm text-stone-400 space-y-1">
                {ag.direccion && <div><MapPin className="w-3 h-3 inline mr-1.5 text-stone-600" /> {ag.direccion}</div>}
                {ag.telefono && <div><Phone className="w-3 h-3 inline mr-1.5 text-stone-600" /> {ag.telefono}</div>}
                {ag.email && <div><Mail className="w-3 h-3 inline mr-1.5 text-stone-600" /> <a href={`mailto:${ag.email}`} className="hover:text-white">{ag.email}</a></div>}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: CTA card */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-gradient-to-br from-[#E0651F]/15 to-stone-900 border border-[#E0651F]/30 rounded-sm p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#E0651F] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-black text-xl text-white leading-tight">¿Te interesa esta subasta?</h3>
                <p className="text-sm text-stone-300 mt-2">No pujes sin un análisis previo de cargas, deudas y situación registral.</p>
              </div>
            </div>
            <div className="space-y-2 mt-6">
              <Link
                to={`/contacto?asunto=${encodeURIComponent("Subasta " + realId)}&mensaje=${encodeURIComponent("Estoy interesado en la subasta " + realId + (direccion ? " (" + direccion : "") + (localidad ? ", " + localidad : "") + (direccion || localidad ? "). " : ". ") + "¿Podríais asesorarme?")}`}
                onClick={() => trackEvent(EVENTS.CTA_CONTACTO, { source: "sidebar_subasta_desktop", subasta_id: realId })}
                className="block bg-[#E0651F] hover:bg-white hover:text-[#1a110e] text-white text-center py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors"
                data-testid="subasta-detail-cta-contacto"
              >
                Contacta para más información
              </Link>
              <Link to="/orientacion" className="block bg-transparent border border-stone-700 hover:border-white text-white text-center py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors">
                Orientación inicial · 15 €
              </Link>
              <Link
                to={`/informe-premium/solicitar?subasta=${encodeURIComponent(realId)}`}
                onClick={() => trackEvent(EVENTS.CTA_INFORME_PREMIUM, { source: "sidebar_subasta_desktop", subasta_id: realId })}
                className="block bg-transparent border border-stone-700 hover:border-white text-white text-center py-3 text-xs tracking-[0.2em] uppercase font-bold rounded-sm transition-colors"
                data-testid="subasta-detail-cta-informe"
              >
                Solicitar Informe Premium ↗
              </Link>
            </div>
            <p className="text-[11px] text-stone-500 mt-5 leading-relaxed">
              Cargas posteriores, ocupación, deudas con comunidad o IBI… Te ayudamos a evaluar si esta oportunidad es realmente buena.
            </p>
          </div>
          <a href={s.url_oficial} target="_blank" rel="noopener noreferrer"
            className="mt-4 block text-center bg-stone-900 hover:bg-stone-800 border border-stone-800 rounded-sm py-3 text-xs tracking-[0.2em] uppercase font-bold text-stone-300 inline-flex items-center justify-center gap-2 w-full">
            Ver ficha oficial en BOE <ExternalLink className="w-3 h-3" />
          </a>
        </aside>
      </section>
      )}

      {/* Disclaimer */}
      <section className="px-6 py-8 border-t border-stone-800">
        <div className="max-w-4xl mx-auto text-xs text-stone-500 leading-relaxed">
          <strong className="text-stone-400">Aviso:</strong> Esta información reproduce datos públicos del Portal de Subastas del BOE
          (<a href={s.url_oficial} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">enlace oficial</a>)
          de conformidad con la Ley 37/2007. Verifique siempre los datos en la fuente oficial antes de pujar.
          Última sincronización: {fmtDate(s.last_seen_at || s.scraped_at)}.
        </div>
      </section>

      <SiteFooter />

      {/* CTA sticky móvil: Informe Premium + WhatsApp. Solo < md. */}
      <MobileStickyCTA realId={realId} direccion={direccion} localidad={localidad} />
    </div>
  );
}

function MobileStickyCTA({ realId, direccion, localidad }) {
  // Mensaje único acordado — sin datos concatenados por página (activador
  // de la programación externa de WhatsApp Business).
  const waMsg = WA_MSG.generic;
  return (
    <>
      {/* Espaciador para que el sticky no tape el disclaimer final. */}
      <div className="md:hidden h-20" aria-hidden="true" />
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-[80] bg-[#1a110e]/95 backdrop-blur-md border-t border-[#3d2820] shadow-[0_-6px_20px_rgba(0,0,0,0.4)] px-3 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))]"
        data-testid="subasta-detail-mobile-sticky-cta"
      >
        <div className="flex items-center gap-2 max-w-lg mx-auto">
          <Link
            to={`/informe-premium/solicitar?subasta=${encodeURIComponent(realId)}`}
            onClick={() => trackEvent(EVENTS.CTA_INFORME_PREMIUM, { source: "sticky_subasta_mobile", subasta_id: realId })}
            className="flex-1 flex items-center justify-center gap-1.5 bg-[#E0651F] hover:bg-white hover:text-[#1a110e] text-white py-3 text-[11px] tracking-[0.15em] uppercase font-bold rounded-sm transition-colors"
            data-testid="mobile-sticky-cta-premium"
          >
            <FileText className="w-4 h-4" /> Informe Premium
          </Link>
          <a
            href={buildWaUrl(waMsg)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              trackEvent(EVENTS.CTA_WHATSAPP, { source: "sticky_subasta_mobile", subasta_id: realId });
              openWhatsApp(waMsg);
            }}
            aria-label="Consultar por WhatsApp"
            className="flex items-center justify-center bg-[#25D366] hover:bg-[#128C7E] text-white w-12 h-12 rounded-sm transition-colors flex-shrink-0"
            data-testid="mobile-sticky-cta-whatsapp"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
          </a>
        </div>
      </div>
    </>
  );
}


function DetailStatusBadge({ status }) {
  const config = {
    terminada: { dot: "bg-red-500", glow: "shadow-[0_0_10px_rgba(239,68,68,0.7)]", txt: "text-red-300", border: "border-red-900/60", text: "Terminada", pulse: false },
    proxima: { dot: "bg-amber-400", glow: "shadow-[0_0_10px_rgba(251,191,36,0.7)]", txt: "text-amber-200", border: "border-amber-900/60", text: "Próxima", pulse: true },
    activa: { dot: "bg-emerald-500", glow: "shadow-[0_0_10px_rgba(16,185,129,0.7)]", txt: "text-emerald-300", border: "border-emerald-900/60", text: "Activa", pulse: true },
    suspendida: { dot: "bg-orange-400", glow: "shadow-[0_0_10px_rgba(251,146,60,0.7)]", txt: "text-orange-200", border: "border-orange-900/60", text: "Activa · Suspendida temporalmente", pulse: true },
  };
  const c = config[status] || config.activa;
  return (
    <span
      className={`inline-flex items-center gap-2 ml-1 px-3 py-1 ${c.txt} font-bold tracking-[0.18em] uppercase rounded-sm bg-stone-950/70 border ${c.border}`}
      data-testid={`subasta-detail-status-${status}`}
    >
      <span className={`w-2.5 h-2.5 rounded-full ${c.dot} ${c.glow} ${c.pulse ? "animate-pulse" : ""}`} />
      {c.text}
    </span>
  );
}

function DataItem({ label, value, highlight, mono }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1">{label}</div>
      <div className={`${highlight ? "text-white font-bold text-lg" : "text-stone-300"} ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}
